import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Zap, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Simple inline Google G logo (keeps bundle small, no extra asset)
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.6 29.4 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.5 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.5 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.4 0 10.3-2.1 14-5.4l-6.5-5.5C29.4 34.9 26.8 36 24 36c-5.4 0-9.9-3.4-11.5-8.2l-6.5 5C9.5 39.7 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.1 4.1-3.9 5.5l6.5 5.5C42.1 36.6 44 30.9 44 24c0-1.2-.1-2.3-.4-3.5z"/>
  </svg>
);

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) {
      toast.success("Bem-vindo!");
      nav("/dashboard");
    } else {
      toast.error(r.error);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-900 text-white relative overflow-hidden">
        <div className="grid-bg absolute inset-0 opacity-30" />
        <div className="relative z-10">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
              <Zap className="w-6 h-6" />
            </div>
            <div>
              <div className="font-display font-bold text-xl leading-none">NEXUS</div>
              <div className="text-xs uppercase tracking-widest text-slate-400">PDV & ERP</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 space-y-6">
          <h1 className="font-display text-4xl xl:text-5xl font-extrabold tracking-tight">
            Venda mais.<br/>Controle tudo.
          </h1>
          <p className="text-slate-300 max-w-md text-base">
            Sistema de PDV, estoque, financeiro e relatórios em uma única plataforma tática.
          </p>
          <div className="grid grid-cols-3 gap-6 text-sm max-w-md">
            <div><div className="kpi-num text-2xl text-emerald-400">98%</div><div className="text-slate-400">uptime</div></div>
            <div><div className="kpi-num text-2xl text-blue-400">4.9x</div><div className="text-slate-400">mais rápido</div></div>
            <div><div className="kpi-num text-2xl text-amber-400">0</div><div className="text-slate-400">fricção</div></div>
          </div>
        </div>
        <div className="relative z-10 text-xs text-slate-500">© 2026 NEXUS PDV</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <Card className="w-full max-w-md card-outline shadow-lg">
          <CardHeader>
            <CardTitle className="font-display text-2xl">Entrar</CardTitle>
            <CardDescription>Acesse sua conta para continuar</CardDescription>
          </CardHeader>
          <CardContent>
            <Button type="button" variant="outline" onClick={handleGoogle}
              className="w-full h-11 mb-4 border-slate-300 hover:bg-slate-50 flex items-center justify-center gap-3"
              data-testid="google-login-btn">
              <GoogleIcon /> <span className="font-medium">Entrar com Google</span>
            </Button>
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-3 text-slate-400">ou com e-mail</span>
              </div>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email">E-mail</Label>
                <Input id="email" data-testid="login-email-input" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} required autoFocus />
              </div>
              <div>
                <Label htmlFor="password">Senha</Label>
                <Input id="password" data-testid="login-password-input" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <div className="flex justify-end">
                <Link to="/forgot-password" className="text-sm text-blue-600 hover:underline" data-testid="forgot-link">
                  Esqueceu a senha?
                </Link>
              </div>
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="login-submit-btn" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Entrar
              </Button>
              <p className="text-xs text-slate-500 text-center pt-2">
                Novo por aqui? Peça acesso ao administrador.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
